import { RefObject, useEffect, useRef, useState } from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import PenIcon from '@cartwhl/ui/icons/PenIcon';
import Button from '@cartwhl/ui/input/Button';

export default function EditableMotionName({
  motionName,
  onSave,
  ref,
}: {
  motionName: string;
  onSave: (name: string) => void;
  ref?: RefObject<HTMLDivElement>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(motionName);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(motionName);
  }, [motionName]);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = async () => {
    setIsEditing(false);
    if (name.trim() !== '') {
      onSave(name);
    } else {
      setName(motionName);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current && !inputRef.current.matches(':focus')) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(0, inputRef.current.value.length);
    }
  }, [inputRef.current, isEditing]);

  return (
    <div ref={ref} className="flex min-w-0 flex-1 items-center">
      {isEditing ? (
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          className="font-display -ml-2 h-10 w-full border-none bg-transparent pt-[0.15em] pl-2 text-left text-base font-medium tracking-tighter outline-hidden"
        />
      ) : (
        <Tooltip text="Rename motion">
          <Button
            variant="tertiary"
            size="lg"
            icon={!isEditing && <PenIcon className="text-gray-60" />}
            alignIcon="end"
            onClick={handleClick}
            className="-ml-2 pl-2"
          >
            <Text as="h1" variant="studio/title/lg" className="truncate">
              {motionName}
            </Text>
          </Button>
        </Tooltip>
      )}
    </div>
  );
}
